import { useEffect, useState, useCallback } from 'react';
import { RootState, AppDispatch } from '../store/store';
import { loadObjectives, updateObjective, deleteObjective } from '../store/admin1Slice';
import { AddEditDeleteBar1 } from '../components_maps/stable/AddEditDeleteBar1';
import { Objective1 } from '../types';
import { useDispatch, useSelector } from 'react-redux';
import { SaveAndRefresh } from '../components_misc/SaveAndRefresh';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export function AdminObjectivesPage() {
  const dispatch = useDispatch<AppDispatch>();
  const objectives = useSelector((state: RootState) => state.admin1.objectives);
  const [editingObjective, setEditingObjective] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<Objective1>>({});

  useEffect(() => {
    if (!objectives?.length) {
      dispatch(loadObjectives());
    }
  }, [dispatch, objectives?.length]);

  const handleEdit = (objective: Objective1) => {
    setEditingObjective(objective.uuid);
    setEditForm(objective);
  };

  const handleSave = async (uuid: string) => {
    await dispatch(updateObjective({ uuid, updates: editForm }));
    setEditingObjective(null);
    setEditForm({});
  };

  const handleDelete = async (uuid: string) => {
    await dispatch(deleteObjective(uuid));
  };

  const handleRefresh = useCallback(async () => {
    await dispatch(loadObjectives());
  }, [dispatch]);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Objective Management</h1>
        <SaveAndRefresh onRefresh={handleRefresh} />
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                UUID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Short ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Text
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Owner
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Additional Info
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {objectives?.map((objective) => (
              <tr key={objective.uuid} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <AddEditDeleteBar1
                    isVisible={true}
                    showAdd={false}
                    onEdit={() => handleEdit(objective)}
                    onDelete={() => handleDelete(objective.uuid)}
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{objective.uuid}</td>
                <td className="px-6 py-4 whitespace-nowrap">{objective.shortId}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingObjective === objective.uuid ? (
                    <input
                      className="border rounded px-2 py-1"
                      value={editForm.text || ''}
                      onChange={(e) => setEditForm({ ...editForm, text: e.target.value })}
                    />
                  ) : (
                    objective.text
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingObjective === objective.uuid ? (
                    <div className="space-x-2">
                      <input
                        className="border rounded px-2 py-1 w-32"
                        value={editForm.ownerName || ''}
                        onChange={(e) => setEditForm({ ...editForm, ownerName: e.target.value })}
                        placeholder="Owner Name"
                      />
                      <input
                        className="border rounded px-2 py-1 w-32"
                        value={editForm.ownerId || ''}
                        onChange={(e) => setEditForm({ ...editForm, ownerId: e.target.value })}
                        placeholder="Owner ID"
                      />
                    </div>
                  ) : (
                    `${objective.ownerName || 'No Owner'} (${objective.ownerId || 'No ID'})`
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingObjective === objective.uuid ? (
                    <input
                      type="date"
                      className="border rounded px-2 py-1"
                      value={editForm.createdDate || ''}
                      onChange={(e) => setEditForm({ ...editForm, createdDate: e.target.value })}
                    />
                  ) : (
                    objective.createdDate
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingObjective === objective.uuid ? (
                    <div className="flex space-x-2">
                      <input
                        className="border rounded px-2 py-1"
                        value={editForm.additionalInfo || ''}
                        onChange={(e) =>
                          setEditForm({ ...editForm, additionalInfo: e.target.value })
                        }
                      />
                      <button
                        className="bg-blue-500 text-white px-2 py-1 rounded"
                        onClick={() => handleSave(objective.uuid)}
                      >
                        Save
                      </button>
                    </div>
                  ) : (
                    objective.additionalInfo || '-'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
